import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  ChartOptions,
  ChartDataset,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import React from 'react';
import { UseQueryResult } from '@tanstack/react-query';
import moment from 'moment';
import {
  dataPointLogic,
  dataSet,
} from '@components/progress/chart/ChartHelper';
import { topicSkillLevel } from '@components/progress/sheet/SelectedTopic';
import NotAvailableState from '@components/progress/chart/NotAvailableState';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
);

const options: ChartOptions<'line'> = {
  scales: {
    y: {
      position: 'right',
      suggestedMax: 100,
      suggestedMin: 0,
      title: {
        text: 'Score',
        display: true,
        color: '#FAFBFD',
        padding: {
          bottom: -5,
        },
      },
      grid: {
        color: '#FAFBFD4C',
      },
      ticks: {
        color: '#FAFBFD',
        stepSize: 20,
      },
    },
    x: {
      grid: {
        color: '#FAFBFD4C',
      },
      border: {
        dash: [5, 5],
      },
      ticks: {
        color: '#FAFBFD',
        autoSkip: false,
      },
    },
  },
  elements: {
    point: {
      radius: 4,
    },
  },
};

export interface LineChartScoreProps {
  xAxisLabel: string[];
  rawData: UseQueryResult[];
  rangeSelected: string | number;
  selectedTopics: topicSkillLevel[];
  isLoading: boolean;
}

const LineChartScore = (props: LineChartScoreProps) => {
  const now = moment();

  // data transformation
  const chartData: ChartDataset<'line', number[]>[] = [];
  let haveValidDataToPlot = false;
  if (props.rawData.length > 0) {
    for (let i = 0; i < props.rawData.length; i++) {
      const item = props.rawData[i].data;
      const dataToPush: number[] = [];
      const date = now.clone();
      let sum = 0;
      let count = 0;
      let haveData = false;

      let rangeSelected: number;
      if (typeof props.rangeSelected === 'string') {
          rangeSelected = parseInt(props.rangeSelected);
      } else {
          rangeSelected = props.rangeSelected;
      }

      for (let j = 0; j <= rangeSelected; j++) {
        const identifier = date.format('YYYY-MM-DD');
        let score = 0;
        if (Array.isArray(item)) {
          const search = item.filter(value => value.date_mark === identifier);
          if (search.length > 0) {
            score = parseFloat(search[0].percentage);
            haveData = true;
            haveValidDataToPlot = true;
          }
        }
        sum += score;
        count++;
        if (dataPointLogic(j, rangeSelected)) {
          dataToPush.unshift(haveData ? sum / count : NaN);
          sum = 0;
          count = 0;
          haveData = false;
        }
        date.subtract(1, 'day');
      }
      const color = props.selectedTopics[i]?.color ?? '#D5ACF3';
      const label = props.selectedTopics[i]?.label ?? 'Total';
      chartData.push(dataSet(dataToPush, color, label));
    }
  }

  return (
    <div className={'relative'}>
      <Line
        options={options}
        datasetIdKey={'id'}
        height={'160px'}
        data={{
          labels: props.xAxisLabel,
          datasets: chartData,
        }}
      />
      {!haveValidDataToPlot && !props.isLoading ? <NotAvailableState /> : null}
    </div>
  );
};

export default LineChartScore;
